import React, { useEffect, useRef, useState } from 'react';
import './CommonStyles.css';
import './ViewSaleModalStyles.css';
import apiService from '../../services/api';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';


const ViewSaleModal = ({ sale, onClose, onPackingSlip, onPrintInvoice, onDownloadEDI }) => {
  const { formatCurrency, formatDate, formatDateTime, quantityPrecision } = useBusinessSettings();
  const contentRef = useRef(null);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [products, setProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [fullSaleData, setFullSaleData] = useState(null);
  const [isLoadingFullData, setIsLoadingFullData] = useState(true);

  // Fetch complete sale data first
  useEffect(() => {
    let isMounted = true;
    
    const fetchFullSaleData = async () => {
      setIsLoadingFullData(true);
      try {
        // If we already have apiData, use it
        if (sale.apiData) {
          setFullSaleData(sale);
          setIsLoadingFullData(false);
        } else {
          // Fetch complete sale details
          const response = await apiService.getSale(sale.id);
          const fullSale = response?.data?.transaction || response?.data?.[0] || response?.data || response;
          const preservedInvoiceUrl = fullSale?.invoice_url || sale?.invoice_url || sale?.invoiceUrl;
          
          if (fullSale && preservedInvoiceUrl) {
            fullSale.invoice_url = preservedInvoiceUrl;
          }
          
          setFullSaleData({ 
            ...sale, 
            invoiceUrl: preservedInvoiceUrl, 
            invoice_url: preservedInvoiceUrl, 
            apiData: fullSale 
          });
          setIsLoadingFullData(false);
        }
      } catch (error) {
        console.error('Error fetching sale details:', error);
        // Fallback to original sale data
        setFullSaleData(sale);
        setIsLoadingFullData(false);
      }
    };
    
    fetchFullSaleData();
    
    return () => { isMounted = false; };
  }, [sale]);

  // Fetch locations, users, and products in background
  useEffect(() => {
    let isMounted = true;
    const apiData = fullSaleData?.apiData || fullSaleData || sale;

    // Fetch locations in background
    apiService.getLocations()
      .then(res => {
        if (!isMounted) return;
        const data = res.data || res;
        if (Array.isArray(data)) setLocations(data);
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setLoadingLocations(false); });

    // Fetch users in background
    apiService.getUsers({ per_page: -1 })
      .then(res => {
        if (!isMounted) return;
        const data = res.data || res;
        if (Array.isArray(data)) setUsers(data);
        else if (data.data && Array.isArray(data.data)) setUsers(data.data);
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setLoadingUsers(false); });

    // Fetch products if sell_lines exist
    const sellLines = apiData.sell_lines || [];
    const productIds = [...new Set(sellLines.map(line => line.product_id).filter(id => id))];

    if (productIds.length > 0) {
      const productDetails = {};
      Promise.all(productIds.map(async (productId) => {
        try {
          const productResponse = await apiService.getProduct(productId);
          const productData = productResponse.data || productResponse;
          const product = Array.isArray(productData) ? productData[0] : productData.data || productData;
          productDetails[productId] = product;
        } catch (_) {}
      })).then(() => {
        if (isMounted) {
          setProducts(productDetails);
          setLoadingProducts(false);
        }
      });
    } else {
      setLoadingProducts(false);
    }

    return () => { isMounted = false; };
  }, [fullSaleData, sale]);

  // Get location name by ID
  const getLocationName = (locationId) => {
    if (!locationId) return 'N/A';
    const location = locations.find(loc => loc.id === parseInt(locationId));
    return location ? location.name : `Location ID: ${locationId}`;
  };

  // Get username by ID
  const getUsername = (userId) => {
    if (!userId) return 'Admin';
    const user = users.find(u => u.id === parseInt(userId));
    return user ? (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || user.name || `User ${userId}`) : `User ${userId}`;
  };

  useEffect(() => {
    // Scroll to top when modal opens
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [sale]);

  if (!sale) {
    return null;
  }

  // Show loading state while fetching complete data
  if (isLoadingFullData) {
    return (
      <div className="modal-overlay">
        <div className="modal-container invoice-modal">
          <div className="modal-header">
            <h2>Loading Sale Details...</h2>
            <button className="modal-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="modal-content invoice-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto 1rem', width: '48px', height: '48px', border: '4px solid #f3f4f6', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              <p style={{ color: '#64748b', fontSize: '0.95rem' }}>Loading complete sale information...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use fullSaleData instead of sale
  const saleToDisplay = fullSaleData || sale;
  const apiData = saleToDisplay.apiData || saleToDisplay;
  const isApiData = !!saleToDisplay.apiData;

  const computeTotalPaid = () => {
    if (isApiData && apiData) {
      if (apiData.total_paid !== undefined && apiData.total_paid !== null) {
        return parseFloat(apiData.total_paid) || 0;
      }
      if (Array.isArray(apiData.payment_lines)) {
        return apiData.payment_lines.reduce((sum, p) => {
          const isBounced = p.is_bounced == 1 || p.is_bounced === true || p.is_bounced === '1';
          return sum + (isBounced ? 0 : (parseFloat(p.amount) || 0));
        }, 0);
      }
    }
    const finalTot = parseFloat(sale.finalTotal || sale.totalPayable || sale.total_payable || 0);
    const due = parseFloat(sale.sellDue || sale.payment_due || 0);
    if (sale.sellDue !== undefined && sale.sellDue !== null && sale.sellDue !== '') {
      return Math.max(0, finalTot - due);
    }
    const statusLower = String(sale.paymentStatus || sale.payment_status || '').toLowerCase();
    if (statusLower === 'due') return 0;
    return finalTot;
  };

  const computeTotalRemaining = () => {
    const finalTot = parseFloat(isApiData ? (apiData.final_total || 0) : (sale.finalTotal || sale.totalPayable || sale.total_payable || 0));
    const paid = computeTotalPaid();
    return Math.max(0, finalTot - paid);
  };

  const resolveInvoiceUrl = () => {
    const rawUrl =
      apiData?.invoice_url ||
      apiData?.invoice_link ||
      apiData?.invoiceUrl ||
      sale?.invoice_url ||
      sale?.invoiceUrl ||
      sale?.invoice_link ||
      sale?.invoiceLink ||
      sale?.apiData?.invoice_url ||
      sale?.apiData?.invoice_link;

    if (rawUrl && typeof rawUrl === 'string' && rawUrl.trim() !== '') {
      return rawUrl.trim();
    }

    const saleId = apiData?.id || sale?.id;
    if (saleId) {
      const host = window.location.host.includes(':3000')
        ? window.location.host.replace(':3000', '')
        : window.location.host;
      return `${window.location.protocol}//${host}/invoice/${saleId}`;
    }

    return null;
  };

  const invoiceUrl = resolveInvoiceUrl();

  const statusStr = String(apiData?.status || sale?.status || '').toLowerCase();
  const subTypeStr = String(apiData?.sub_type || sale?.sub_type || '').toLowerCase();
  const typeStr = String(apiData?.type || sale?.type || '').toLowerCase();

  const isDraftOrQuotation =
    statusStr === 'draft' ||
    statusStr === 'quotation' ||
    statusStr === 'proforma' ||
    subTypeStr === 'draft' ||
    subTypeStr === 'quotation' ||
    subTypeStr === 'proforma' ||
    typeStr === 'draft' ||
    typeStr === 'quotation' ||
    typeStr === 'sell_draft' ||
    Boolean(sale?.is_draft) ||
    Boolean(sale?.is_quotation) ||
    Boolean(apiData?.is_draft) ||
    Boolean(apiData?.is_quotation);

  return (
    <div className="modal-overlay">
      <div className="modal-container invoice-modal">
        <div className="modal-header">
          <h2>Sell Details ( Invoice No. : {isApiData ? apiData.invoice_no : saleToDisplay.invoiceNo} )</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-content invoice-content" ref={contentRef}>
          {/* Invoice Header */}
          <div className="invoice-header-section">
            <div className="invoice-info-item">
              <strong>Invoice No. :</strong> #{isApiData ? apiData.invoice_no : saleToDisplay.invoiceNo}
            </div>
            <div className="invoice-info-item">
              <strong>Date:</strong> {isApiData ? formatDate(apiData.transaction_date) : formatDate(saleToDisplay.saleDate)}
            </div>
            <div className="invoice-info-item">
              <strong>Status:</strong> {isApiData ? apiData.status : saleToDisplay.status}
            </div>
            <div className="invoice-info-item">
              <strong>Payment Status:</strong> {isApiData ? apiData.payment_status : saleToDisplay.paymentStatus}
            </div>
            <div className="invoice-info-item">
              <strong>Location:</strong> {loadingLocations ? 'Loading...' : getLocationName(isApiData ? apiData.location_id : saleToDisplay.locationId)}
            </div>
          </div>

          {/* Customer and Service Info */}
          <div className="customer-service-section">
            <div className="customer-info-box">
              <div className="info-item">
                <strong>Customer name:</strong> {isApiData ? apiData.contact?.name : saleToDisplay.customer}
              </div>
              <div className="info-item">
                <strong>Address:</strong>
                <div>{isApiData ? (apiData.contact?.address_line_1 || '--') : (saleToDisplay.billingAddress || '--')}</div>
              </div>
              <div className="info-item">
                <strong>Mobile:</strong> {isApiData ? (apiData.contact?.mobile || '--') : (saleToDisplay.contactNumber || '--')}
              </div>
            </div>
            <div className="service-info-box">
              <div className="info-item">
                <strong>Service staff:</strong>
                <div>{isApiData ? (apiData.service_staff || '--') : (saleToDisplay.serviceStaff || '--')}</div>
              </div>
              <div className="info-item">
                <strong>Shipping:</strong>
                <div>{isApiData ? (apiData.shipping_status || '--') : (sale.shippingStatus || '--')}</div>
              </div>
            </div>
          </div>

          {/* Shipping Details Section */}
          {(isApiData ? (apiData.shipping_address || apiData.shipping_city || apiData.shipping_state) :
            (saleToDisplay.shippingAddress || saleToDisplay.shippingCity || saleToDisplay.shippingState)) && (
              <div className="invoice-section">
                <h3>Shipping Details:</h3>
                <div className="shipping-info-box">
                  <div className="info-item">
                    <strong>Shipping Address:</strong>
                    <div>
                      {isApiData ? (apiData.shipping_address || '--') : (saleToDisplay.shippingAddress || '--')}
                    </div>
                  </div>
                  <div className="info-item">
                    <strong>City:</strong>
                    <div>{isApiData ? (apiData.shipping_city || '--') : (saleToDisplay.shippingCity || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>State:</strong>
                    <div>{isApiData ? (apiData.shipping_state || '--') : (saleToDisplay.shippingState || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>Country:</strong>
                    <div>{isApiData ? (apiData.shipping_country || '--') : (saleToDisplay.shippingCountry || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>ZIP Code:</strong>
                    <div>{isApiData ? (apiData.shipping_zip_code || '--') : (saleToDisplay.shippingZipCode || '--')}</div>
                  </div>
                  <div className="info-item">
                    <strong>Shipping Status:</strong>
                    <div>{isApiData ? (apiData.shipping_status || '--') : (saleToDisplay.shippingStatus || '--')}</div>
                  </div>
                </div>
              </div>
            )}

          {/* Products Section */}
          <div className="invoice-section">
            <h3>Products:</h3>
            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>UPC / SKU</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Discount</th>
                    <th>Tax</th>
                    <th>Price inc. tax</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {isApiData && apiData.sell_lines ? (
                    apiData.sell_lines.map((line, index) => {
                      const product = products[line.product_id];
                      return (
                        <tr key={line.id}>
                          <td>{index + 1}</td>
                          <td>{product?.name || line.product?.name || `Product #${line.product_id}`}</td>
                          <td>{product?.sku || product?.barcode || line.product?.sku || line.product?.barcode || '--'}</td>
                          <td>{parseFloat(line.quantity).toFixed(quantityPrecision)} {line.unit || 'Single'}</td>
                          <td>{formatCurrency(line.unit_price)}</td>
                          <td>{formatCurrency(line.line_discount_amount || 0)}</td>
                          <td>{formatCurrency(line.item_tax)} {line.tax_name ? `( ${line.tax_name} )` : ''}</td>
                          <td>{formatCurrency(line.unit_price_inc_tax)}</td>
                          <td>{formatCurrency((parseFloat(line.unit_price_inc_tax) * parseFloat(line.quantity)).toFixed(2))}</td>
                        </tr>
                      );
                    })
                  ) : saleToDisplay.products && saleToDisplay.products.length > 0 ? (
                    saleToDisplay.products.map((product, index) => (
                      <tr key={product.id}>
                        <td>{index + 1}</td>
                        <td>{product.name}</td>
                        <td>{product.sku || '--'}</td>
                        <td>{product.quantity} Single</td>
                        <td>{formatCurrency(product.unitPrice)}</td>
                        <td>{formatCurrency(product.discount || 0)}</td>
                        <td>{formatCurrency(0)} {product.tax ? `( ${product.tax} )` : ''}</td>
                        <td>{formatCurrency(product.priceIncTax)}</td>
                        <td>{formatCurrency(product.subtotal)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center' }}>No products found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Info and Totals Combined Section */}
          <div className="payment-totals-wrapper">
            {/* Payment Info Section */}
            <div className="invoice-section payment-info-section">
              <h3>Payment info:</h3>
              <div className="invoice-table-wrapper">
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Reference No</th>
                      <th>Amount</th>
                      <th>Check Return Charges</th>
                      <th>Payment mode</th>
                      <th>Payment note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isApiData && apiData.payment_lines && apiData.payment_lines.length > 0 ? (
                      apiData.payment_lines.map((payment, index) => (
                        <tr key={payment.id || index}>
                          <td>{index + 1}</td>
                          <td>{formatDate(payment.paid_on)}</td>
                          <td>{payment.payment_ref_no || '--'}</td>
                          <td>{formatCurrency(payment.amount)}</td>
                          <td>{formatCurrency(payment.check_return_charges || 0)}</td>
                          <td>{payment.method}</td>
                          <td>{payment.note || '--'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center' }}>No payments found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals Section */}
            <div className="totals-section">
              <div className="totals-grid">
                <div className="total-row">
                  <span className="total-label">Total:</span>
                  <span className="total-value">{isApiData ? formatCurrency(apiData.total_before_tax) : formatCurrency(saleToDisplay.productAmount || saleToDisplay.totalPayable)}</span>
                </div>
                {isApiData && apiData.check_return_charges && parseFloat(apiData.check_return_charges) > 0 && (
                  <div className="total-row">
                    <span className="total-label">Check Return Charges:</span>
                    <span className="total-value">{formatCurrency(apiData.check_return_charges)}</span>
                  </div>
                )}
                <div className="total-row">
                  <span className="total-label">Discount:</span>
                  <span className="total-value">(-) {isApiData ? (apiData.discount_type === 'percentage' ? `${apiData.discount_amount}%` : formatCurrency(apiData.discount_amount)) : formatCurrency(saleToDisplay.discountAmount || 0)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Order Tax:</span>
                  <span className="total-value">(+) {isApiData ? formatCurrency(apiData.tax_amount || 0) : formatCurrency(saleToDisplay.orderTaxAmount || 0)}</span>
                </div>
                {isApiData && apiData.sell_lines && (
                  <div className="total-row">
                    <span className="total-label">Line taxes:</span>
                    <span className="total-value">
                      {apiData.sell_lines.map(line => line.tax_name).filter((v, i, a) => a.indexOf(v) === i).join(', ') || '--'} -
                      {formatCurrency(apiData.sell_lines.reduce((sum, line) => sum + parseFloat(line.item_tax || 0), 0))}
                    </span>
                  </div>
                )}
                <div className="total-row">
                  <span className="total-label">Shipping:</span>
                  <span className="total-value">(+) {isApiData ? formatCurrency(apiData.shipping_charges || 0) : formatCurrency(saleToDisplay.shippingCharges || 0)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Round Off:</span>
                  <span className="total-value">{isApiData ? formatCurrency(apiData.round_off || 0) : formatCurrency(saleToDisplay.roundOff || 0)}</span>
                </div>
                <div className="total-row total-payable-row">
                  <span className="total-label">Total Payable:</span>
                  <span className="total-value">{isApiData ? formatCurrency(apiData.final_total) : formatCurrency(saleToDisplay.totalPayable)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Total paid:</span>
                  <span className="total-value">{formatCurrency(computeTotalPaid())}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Total remaining:</span>
                  <span className="total-value">
                    {formatCurrency(computeTotalRemaining())}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Public Share Link if available */}
          {(apiData.public_url || apiData.invoice_url || apiData.invoice_link) && (
            <div style={{ marginTop: '1.25rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  readOnly
                  value={apiData.public_url || apiData.invoice_url || apiData.invoice_link}
                  style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', background: '#fff', color: '#334155' }}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(apiData.public_url || apiData.invoice_url || apiData.invoice_link);
                    alert('Copied link to clipboard!');
                  }}
                  style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  📋 Copy
                </button>
                <button
                  onClick={() => window.open(apiData.public_url || apiData.invoice_url || apiData.invoice_link, '_blank')}
                  style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  🔗 Open
                </button>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="modal-notes-grid">
            <div className="note-block">
              <h3>Sell Note</h3>
              <p>{isApiData ? (apiData.additional_notes || '--') : (saleToDisplay.sellNote || '--')}</p>
            </div>
            <div className="note-block">
              <h3>Staff Note</h3>
              <p>{isApiData ? (apiData.staff_note || '--') : (saleToDisplay.staffNote || '--')}</p>
            </div>
          </div>

          {/* Activities / Audit Trail */}
          <div style={{ marginTop: '1.5rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>Activities:</h3>
            <table className="modal-data-table">
              <thead>
                <tr style={{ background: '#10b981', color: '#ffffff' }}>
                  <th style={{ color: '#ffffff' }}>Date</th>
                  <th style={{ color: '#ffffff' }}>Action</th>
                  <th style={{ color: '#ffffff' }}>By</th>
                  <th style={{ color: '#ffffff' }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {isApiData && apiData.activities && apiData.activities.length > 0 ? (
                  apiData.activities.map((act, idx) => (
                    <tr key={idx}>
                      <td>{formatDateTime(act.created_at)}</td>
                      <td>{act.description || act.action || 'Added'}</td>
                      <td>{act.created_by_user ? `${act.created_by_user.first_name || ''} ${act.created_by_user.last_name || ''}`.trim() : (act.user_name || 'Admin')}</td>
                      <td>{act.note || `Status: ${apiData.status || 'draft'}\nTotal: $ ${parseFloat(apiData.final_total || 0).toFixed(2)}\nPayment Status: ${apiData.payment_status || ''}`}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>{formatDateTime(saleToDisplay.date || new Date())}</td>
                    <td>Added</td>
                    <td>{getUsername(saleToDisplay.addedBy)}</td>
                    <td style={{ whiteSpace: 'pre-line' }}>{`Status: ${saleToDisplay.status || 'draft'}
Total: $ ${parseFloat(saleToDisplay.finalTotal || 0).toFixed(2)}
Payment Status: ${saleToDisplay.paymentStatus || ''}`}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn packing-slip-btn" onClick={() => onPackingSlip && onPackingSlip(saleToDisplay)}>
            📄 Packing Slip
          </button>
          <button
            className="modal-btn print-invoice-btn"
            onClick={() => {
              if (onPrintInvoice) {
                onPrintInvoice(saleToDisplay);
              } else if (invoiceUrl) {
                window.open(invoiceUrl, '_blank');
              } else {
                alert('Invoice URL not available for this sale.');
              }
            }}
          >
            🖨️ Print Invoice
          </button>
          {onDownloadEDI && !isDraftOrQuotation && (
            <button
              className="modal-btn download-edi-btn"
              onClick={() => onDownloadEDI(saleToDisplay)}
            >
              📥 Download EDI
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewSaleModal;
